import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {filter, first, mergeMap, tap} from 'rxjs/operators';
import {
  AddOfferings,
  AddProduct,
  LicenseType,
  Messages,
  Product,
  ProductOffering, ProductOfferingStatus,
  ProductService,
  Status,
  UserService
} from '@uofu-uss-aux/software-catalog-ng-core';
import {Observable, of, Subscription, zip} from 'rxjs';
import {Graphic, GraphicService} from '../../graphic/graphic.service';
import {Cart} from '../../shopping-cart/Cart';
import {CartService, InvalidItem} from '../../shopping-cart/cart.service';
import {NotificationService} from '@uofu-uss/angular-util';
import {LicensingService} from '../licensing.service';
import {DatePipe, NgIf} from '@angular/common';
import {LicenseInfoComponent} from '../license-info/license-info.component';
import {LicenseExpirationWarningComponent} from '../license-expiration-warning/license-expiration-warning.component';
import {ProductDetailsByLicenseComponent} from '../product-details-by-license/product-details-by-license.component';
import {PurchaseNotAllowedWarningComponent} from '../purchase-not-allowed-warning/purchase-not-allowed-warning.component';
import {CaretRightIconComponent} from '../../svg/caret-right-icon/caret-right-icon.component';
import {CatalogGraphicComponent} from '../../graphic/catalog-graphic/catalog-graphic.component';

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    LicenseInfoComponent,
    LicenseExpirationWarningComponent,
    ProductDetailsByLicenseComponent,
    PurchaseNotAllowedWarningComponent,
    DatePipe,
    CaretRightIconComponent,
    CatalogGraphicComponent,
  ]
})
export class ProductDetailsComponent extends AddProduct(AddOfferings(Object)) implements OnInit, OnDestroy {

  graphic: Graphic;
  anyOffering: ProductOffering | undefined;
  allOfferings: ReadonlyArray<ProductOffering> = [];
  hasAnyWithUpgradeProtectionIncluded = false;
  hasAnyWithUpgradeProtectionOnly = false;
  licenseStartDate: string;
  licenseEndDate: string;

  displayStudentEligibilityError = false;
  displayLicenseErrors = {
    student: false,
    employee: false,
    university: false
  };

  private subscription: Subscription;

  constructor(private readonly route: ActivatedRoute,
              private readonly productService: ProductService,
              private readonly notificationService: NotificationService,
              private readonly graphicService: GraphicService,
              private readonly userService: UserService,
              private readonly cartService: CartService,
              private readonly router: Router,
              private readonly ngZone: NgZone,
              private readonly licensingService: LicensingService) {
    super();
  }

  ngOnInit(): void {
    this.subscription = this.route.paramMap.subscribe(pm => {
      const id: number = +pm.get('id');
      this.fetchProduct(id);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  loadOtherLicenses(): boolean {
    return this.userService.value?.employee;
  }

  fetchProduct(id: number) {
    const observer = {
      next: input => this.loadSuccess(input[0], input[1]),
      error: this.loadError,
    };
    return this.productService.fetchProduct(id, Status.Active, ProductOfferingStatus.Active)
      .pipe(
        first(),
        mergeMap(p => zip(of(p), p.imageId ? this.fetchGraphic(p.imageId) : of(null))))
      .subscribe(observer);
  }

  addToCart(offering: ProductOffering) {
    const observer = {
      next: this.addToCartSuccess,
      error: this.addToCartError,
    };
    this.cartService.canBeAddedToCart(this.cartService.value, offering, this.product, this.userService.value)
      .pipe(
        first(),
        tap(result => {
          this.displayStudentEligibilityError = !result.canBeAdded && result.type === InvalidItem.Student;
          if (!result.canBeAdded && result.type === InvalidItem.License) {
            this.displayLicenseErrors.student = this.studentOfferings.includes(offering);
            this.displayLicenseErrors.employee = this.employeeOfferings.includes(offering);
            this.displayLicenseErrors.university = this.universityOfferings.includes(offering);
          }
        }),
        filter(result => result.canBeAdded),
        mergeMap(() => {
          return this.cartService.addToCart(offering, this.cartService.value);
        })
      )
      .subscribe(observer);
  }

  readonly addToCartError = (errorResponse) => {
    this.notificationService.clear();
    const body = Messages.validationMsg(errorResponse, 'Unable to add product to cart');
    this.notificationService.addError({
      title: 'Error',
      body,
    });
  }

  readonly addToCartSuccess = (cart: Cart) => {
    this.cartService.value = cart;
    this.ngZone.run(() => this.router.navigate(['cart']));
  }

  private fetchGraphic(id: number): Observable<Graphic> {
    return this.graphicService.fetchGraphic(id);
  }

  readonly loadError = () => {
    this.notificationService.addError(Messages.LOAD_PROD_MSG);
  }

  readonly loadSuccess = (product: Product, graphic: Graphic) => {
    this.product = product;
    this.studentOfferings = this.productService.findLicense(product, LicenseType.Student)?.offerings || [];
    if (this.loadOtherLicenses()) {
      this.employeeOfferings = this.productService.findLicense(product, LicenseType.Employee)?.offerings || [];
      this.universityOfferings = this.productService.findLicense(product, LicenseType.University)?.offerings || [];
    }
    this.allOfferings = this.studentOfferings.concat(this.employeeOfferings).concat(this.universityOfferings);
    this.hasAnyWithUpgradeProtectionIncluded = this.licensingService.anyOfferingsHaveProtectionIncluded(this.allOfferings);
    this.hasAnyWithUpgradeProtectionOnly = this.licensingService.anyOfferingsHaveProtectionOnly(this.allOfferings);
    this.anyOffering = this.getAnyOffering();
    this.graphic = graphic;
    this.licenseStartDate = this.licensingService.pickEarliestStartDate(this.allOfferings)?.toDateString() || '';
    this.licenseEndDate = this.licensingService.pickEarliestEndDate(this.allOfferings)?.toDateString() || '';
  }

}
